import apiClient from '../client';
import {
  LoanProduct,
  LoanCalculationRequest,
  LoanCalculationResponse,
  LoanApplicationRequest,
  LoanApplication,
  SubmitApplicationResponse,
  ApplicationApprovalRequest,
  ApplicationRejectionRequest,
  Loan,
  PaymentScheduleItem,
  LoanTransaction,
  LoanQueryParams,
  ApplicationQueryParams,
} from '../types/loan.types';

/**
 * Loan and Application API endpoints
 */
export const loansApi = {
  // ==================== PRODUCTS ====================

  /**
   * List all loan products
   */
  listProducts: async (): Promise<LoanProduct[]> => {
    const response = await apiClient.get<{ success: boolean; products: LoanProduct[] }>('/products');
    return response.data.products;
  },

  /**
   * Get single product details
   */
  getProduct: async (productId: string): Promise<LoanProduct> => {
    const response = await apiClient.get<{ success: boolean; product: LoanProduct }>('/products', {
      params: { id: productId }
    });
    return response.data.product;
  },

  /**
   * Create new loan product (Admin only)
   */
  createProduct: async (data: import('../types/loan.types').LoanProductRequest): Promise<import('../types/loan.types').CreateLoanProductResponse> => {
    const response = await apiClient.post<import('../types/loan.types').CreateLoanProductResponse>('/products', data);
    return response.data;
  },

  /**
   * Update loan product (Admin only)
   */
  updateProduct: async (productId: string, data: import('../types/loan.types').LoanProductRequest): Promise<import('../types/loan.types').UpdateLoanProductResponse> => {
    const response = await apiClient.put<import('../types/loan.types').UpdateLoanProductResponse>('/products', data, {
      params: { id: productId }
    });
    return response.data;
  },

  /**
   * Delete/Disable loan product (Admin only)
   */
  deleteProduct: async (productId: string): Promise<import('../types/loan.types').DeleteLoanProductResponse> => {
    const response = await apiClient.delete<import('../types/loan.types').DeleteLoanProductResponse>('/products', {
      params: { id: productId }
    });
    return response.data;
  },

  // ==================== APPLICATIONS ====================

  /**
   * Calculate loan terms
   */
  calculateLoan: async (data: LoanCalculationRequest): Promise<LoanCalculationResponse> => {
    const response = await apiClient.post<{ success: boolean; calculation: LoanCalculationResponse }>(
      '/loans/applications/calculate',
      data
    );
    return response.data.calculation;
  },

  /**
   * Submit loan application
   */
  submitApplication: async (data: LoanApplicationRequest): Promise<SubmitApplicationResponse> => {
    const response = await apiClient.post<SubmitApplicationResponse>(
      '/loans/applications/apply',
      data
    );
    return response.data;
  },

  /**
   * List all applications
   */
  listApplications: async (params?: ApplicationQueryParams): Promise<{ totalApplications: number; applications: LoanApplication[] }> => {
    const response = await apiClient.get<{ totalApplications: number; applications: LoanApplication[] }>(
      '/loans/applications',
      { params }
    );
    return response.data;
  },

  /**
   * Get application details
   */
  getApplication: async (applicationId: string): Promise<LoanApplication> => {
    const response = await apiClient.get<{ application: LoanApplication }>(
      `/loans/applications/${applicationId}`
    );
    return response.data.application;
  },

  /**
   * Approve application
   */
  approveApplication: async (applicationId: string, data: ApplicationApprovalRequest): Promise<{ approved: boolean; loanId: string }> => {
    const response = await apiClient.post<{ approved: boolean; loanId: string }>(
      `/loans/applications/${applicationId}/approve`,
      data
    );
    return response.data;
  },

  /**
   * Reject application
   */
  rejectApplication: async (applicationId: string, data: ApplicationRejectionRequest): Promise<{ success: boolean }> => {
    const response = await apiClient.post<{ success: boolean }>(
      `/loans/applications/${applicationId}/reject`,
      data
    );
    return response.data;
  },

  /**
   * Retrigger M-Pesa fee payment prompt (v1.1.0)
   * Use when application is in PENDING_FEE_PAYMENT status and customer did not receive the STK push
   */
  retriggerFeePayment: async (applicationId: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post<{ success: boolean; message: string }>(
      `/loans/applications/${applicationId}/retrigger-fee`
    );
    return response.data;
  },

  // ==================== ACTIVE LOANS ====================

  /**
   * List all loans
   */
  list: async (params?: LoanQueryParams): Promise<Loan[]> => {
    const response = await apiClient.get<{ loans: Loan[] }>('/loans', { params });
    return response.data.loans;
  },

  /**
   * Get loan details
   */
  getById: async (loanId: string): Promise<Loan> => {
    const response = await apiClient.get<{ loan: Loan }>(`/loans/${loanId}`);
    return response.data.loan;
  },

  /**
   * Get payment schedule
   */
  getSchedule: async (loanId: string): Promise<PaymentScheduleItem[]> => {
    const response = await apiClient.get<{ schedule: PaymentScheduleItem[] }>(
      `/loans/${loanId}/schedule`
    );
    return response.data.schedule;
  },

  /**
   * Get loan transactions
   */
  getTransactions: async (loanId: string): Promise<LoanTransaction[]> => {
    const response = await apiClient.get<{ transactions: LoanTransaction[] }>(
      `/loans/${loanId}/transactions`
    );
    return response.data.transactions;
  },
};
